import get from "lodash/get";

import type { Action } from "../../actions/types";
import { StoreState as LedgerHead } from "../reward/ledgerHead";

export type RewardConfigRewardType =
  | "discount"
  | "credit"
  | "giftCard"
  | "cash"
  | "product"
  | "other"
  | "ledger"
  | "integration"
  | "custom"
  | "shopifyFreeGift";

export type RewardConfigDelivery =
  | "codeBank"
  | "deposit"
  | "manual"
  | "other"
  | "ledger"
  | "integration"
  | "custom";

export interface RewardTierConfig {
  rewardType: RewardConfigRewardType;
  delivery: RewardConfigDelivery;
  shopifyFreeGiftConfig?: ShopifyFreeGiftConfig;
}

export type ShopifyFreeGiftConfig = {
  productVariant: { id: string; title: string; sku: string };
  fallbackProductVariant?: { id: string; title: string; sku: string };
  limitPerOrder: "Unlimited" | number;
  inventoryAlertThreshold?: "Never Alert" | number;
};

export interface ExchangeRecord {
  id: string;
  exchangeType: string;
  name: string;
  status: string;
  ledgerCurrency: string;
  ledgerAmount: number;
  amount?: number;
  default?: boolean;
  createdOn: string;
  updatedOn?: string;
  reward?: RewardTierConfig;
  imageSrc?: string;
  displayImage?: boolean;
  position?: number;
}

export type StoreState = {
  exchanges?: ExchangeRecord[];
  selectedExchange?: ExchangeRecord;
  exchangeId?: string;
  ledgerHead?: LedgerHead;
  newBalance?: string; // based on user's ledgerHead and exchange.ledgerAmount
};

export const initialState: StoreState = {};

const getNewBalance = (exchange: ExchangeRecord, ledger: LedgerHead) => {
  const newBalance =
    Number(get(ledger, "balance", 0)) - get(exchange, "ledgerAmount", 0);

  return Number.isInteger(newBalance)
    ? newBalance.toString()
    : newBalance.toFixed(2);
};

// tslint:disable:cyclomatic-complexity
export function reducer(
  state: StoreState = initialState,
  action: Action
): StoreState {
  let selectedExchange;
  let newBalance;

  if (action.type === "GET_EXCHANGES_SUCCESS") {
    const exchanges = action.response.data.exchanges;

    if (state.exchangeId) {
      selectedExchange = exchanges.find(
        (exchange: ExchangeRecord) => exchange.id === state.exchangeId
      );
      if (selectedExchange) {
        // for env var convenience
        selectedExchange.amount = selectedExchange.ledgerAmount;

        if (state.ledgerHead) {
          newBalance = getNewBalance(selectedExchange, state.ledgerHead);
        }
      }
    }

    return {
      ...state,
      exchanges,
      selectedExchange,
      newBalance,
    };
  }

  if (action.type === "SET_EXCHANGE_ID") {
    const exchangeId = action.data;

    if (state.exchanges && state.exchanges.length > 0) {
      selectedExchange = state.exchanges.find(
        (exchange: ExchangeRecord) => exchange.id === exchangeId
      );
      if (selectedExchange) {
        selectedExchange.amount = selectedExchange.ledgerAmount;

        if (state.ledgerHead) {
          newBalance = getNewBalance(selectedExchange, state.ledgerHead);
        }
      }
    }

    return {
      ...state,
      exchangeId,
      selectedExchange,
      newBalance,
    };
  }

  if (action.type === "LOYALTY_GET_LEDGER_SUCCESS") {
    const ledgerHead = action.response.data;
    if (state.selectedExchange) {
      newBalance = getNewBalance(state.selectedExchange, ledgerHead);
    }

    return {
      ...state,
      ledgerHead,
      newBalance,
    };
  }

  return state;
}
